<template>
  <div v-if="loaded">
    <SmartStickyHeader>
      <div class="mobile-nav">
        <select
          @change="updateRoute()"
          v-model="selectedPoolId"
          class="round-selector"
        >
          <option :value="null">Standings</option>
          <template v-for="(round, roundIndex) in poolData.round.event.rounds">
            <template v-if="round.pools.length === 1">
              <option :value="round.pools[0].id">R{{ roundIndex + 1 }}</option>
            </template>
            <template v-else>
              <option :value="pool.id" v-for="(pool, poolIndex) in round.pools">
                R{{ roundIndex + 1
                }}<template v-if="pool.name"> - {{ pool.name }}</template
                ><template v-else
                  >P{{ $store.state.poolConversion[poolIndex] }}</template
                >
              </option>
            </template>
          </template>
        </select>
        <select
          :disabled="selectedDivisionId === null"
          v-model="selectedDivisionId"
          @change="updateRoute()"
          class="division-selector"
        >
          <option v-if="selectedDivisionId === null" :value="null">
            No divisions yet
          </option>
          <option :value="division.id" v-for="division in availableDivisions">
            {{ division.name }}
          </option>
        </select>
      </div>
    </SmartStickyHeader>
    <div class="desktop-nav">
      <CustomDropDown
        class="round-select"
        position="left"
        :items="roundsItems"
        :disabled="roundsItems.length === 1"
        @changeRound="
          selectedPoolId = $event
          updateRoute()
        "
      >
        <div class="selector-wrapper">
          <p>
            <b>{{ selectedPoolName }}</b>
          </p>
          <ChevronFat direction="down" v-if="roundsItems.length > 1" />
        </div>
      </CustomDropDown>
      <CustomDropDown
        position="left"
        :items="divisionItems"
        :disabled="divisionItems.length === 1"
        @changeDivision="
          selectedDivisionId = $event
          updateRoute()
        "
      >
        <div
          class="selector-wrapper"
          :class="{ inactive: this.availableDivisions.length === 0 }"
        >
          <p>
            <b>{{ selectedDivisionName }}</b>
          </p>
          <ChevronFat direction="down" v-if="divisionItems.length > 1" />
        </div>
      </CustomDropDown>
    </div>
    <PublicFinishedPoolResults
      v-if="
        poolData.status == 'COMPLETED' &&
        poolData.round.event.scoringAs === 'SINGLES'
      "
      @moveDivision="
        selectedDivisionId = $event
        updateRoute()
      "
      @updateDivision="selectedDivisionId = $event"
    />
    <PublicFinishedPoolTeamResults
      v-else-if="poolData.status == 'COMPLETED'"
      @moveDivision="
        selectedDivisionId = $event
        updateRoute()
      "
      @updateDivision="selectedDivisionId = $event"
    />
    <PublicEventLivePool
      v-else-if="
        (poolData.status == 'OPEN' || poolData.status == 'PAUSE') &&
        poolData.round.event.scoringAs === 'SINGLES'
      "
      @moveDivision="
        selectedDivisionId = $event
        updateRoute()
      "
      @updateDivision="selectedDivisionId = $event"
    />
    <PublicEventTeamLivePool
      v-else-if="poolData.status == 'OPEN' || poolData.status == 'PAUSE'"
      @moveDivision="
        selectedDivisionId = $event
        updateRoute()
      "
      @updateDivision="selectedDivisionId = $event"
    />
    <div class="placeholder-message" v-else>
      <PublicPagesPlaceholder
        v-hammer:swipe="(event) => moveDivision(event)"
        class="no-rounds-finished"
        :title="
          poolData.round.pools.length > 1
            ? 'Pool not played yet'
            : 'Round not played yet'
        "
        text="Once a round is started the results will automagically be available."
      />
    </div>
  </div>
</template>

<script>
import SmartStickyHeader from '@/components/SmartStickyHeader.vue'
import PublicFinishedPoolResults from '@/pages/PublicFinishedPoolResults'
import PublicEventLivePool from '@/pages/PublicEventLivePool'
import PublicEventTeamLivePool from '@/pages/PublicEventTeamLivePool'
import PublicFinishedPoolTeamResults from '@/pages/PublicFinishedPoolTeamResults'
import CustomDropDown from '@/components/CustomDropDown.vue'
import ChevronFat from '@/assets/icons/ChevronFat'
import PublicPagesPlaceholder from '@/pages/manager/players/PublicPagesPlaceholder.vue'
export default {
  name: 'PublicEventPoolResults',
  components: {
    SmartStickyHeader,
    PublicFinishedPoolResults,
    PublicEventLivePool,
    PublicEventTeamLivePool,
    PublicFinishedPoolTeamResults,
    CustomDropDown,
    ChevronFat,
    PublicPagesPlaceholder,
  },
  data() {
    return {
      selectedDivisionId: null,
      selectedPoolId: null,
      poolData: null,
      loaded: false,
    }
  },
  watch: {
    $route: function (to, from) {
      if (to.params.poolId !== from.params.poolId) {
        this.loaded = false
      }
      this.loadData()
    },
  },
  computed: {
    selectedDivisionName() {
      if (this.availableDivisions.length === 0) {
        return 'No divisions yet'
      }

      const division = this.availableDivisions.find(
        (division) => division.id === this.selectedDivisionId
      )

      return division ? division.name : this.availableDivisions[0].name
    },
    selectedPoolName() {
      let currentRound
      let roundIndex
      let poolName
      let poolIndex
      let multiPoolRound
      this.poolData.round.event.rounds.forEach((round, index) => {
        if (round.pools.find((pool) => pool.id === this.selectedPoolId)) {
          currentRound = round
          roundIndex = index
        }
      })

      if (!currentRound) {
        return 'Standings'
      }

      currentRound.pools.forEach((pool, index) => {
        if (pool.id === this.selectedPoolId) {
          poolName = pool.name
          poolIndex = index
          multiPoolRound = currentRound.pools.length > 1
        }
      })

      return multiPoolRound
        ? `R${roundIndex + 1}${
            poolName
              ? ' - ' + poolName
              : 'P' + this.$store.state.poolConversion[poolIndex]
          }`
        : `Round ${roundIndex + 1}`
    },
    roundsItems() {
      const items = [
        {
          title: 'Standings',
          action: 'changeRound',
          value: null,
          selected: this.selectedDivisionId === null,
        },
      ]

      this.poolData.round.event.rounds.forEach((round, roundIndex) => {
        if (round.pools.length === 1) {
          items.push({
            title: `Round ${roundIndex + 1}`,
            action: 'changeRound',
            value: round.pools[0].id,
            selected: this.selectedPoolId === round.pools[0].id,
          })
        } else {
          round.pools.forEach((pool, poolIndex) => {
            items.push({
              title: `Round ${roundIndex + 1}${
                pool.name
                  ? ` - ${pool.name}`
                  : ` - Pool ${this.$store.state.poolConversion[poolIndex]}`
              }`,
              action: 'changeRound',
              value: pool.id,
              selected: this.selectedPoolId === pool.id,
            })
          })
        }
      })

      return items
    },
    divisionItems() {
      const items = []

      if (this.availableDivisions.length === 0) {
        items.push({
          title: 'No divisions yet',
          action: 'changeDivision',
          value: null,
          selected: true,
        })
        return items
      }
      items.push(
        ...this.availableDivisions.map((division) => {
          return {
            title: division.name,
            action: 'changeDivision',
            value: division.id,
            selected: this.selectedDivisionId === division.id,
          }
        })
      )
      return items
    },
    availableDivisions() {
      return this.eventDivisionPools.divisions.filter((division) =>
        this.divisionsInPool.map((d) => d.divisionId).includes(division.id)
      )
    },
    divisionsInPool() {
      return this.eventDivisionPools.pools.filter(
        (division) => division.poolId == this.poolData.id
      )
    },
    eventDivisionPools() {
      return this.$store.state.eventDivisionPools
    },
  },
  methods: {
    moveDivision(e) {
      if (this.availableDivisions.length > 1) {
        const currentIndex = this.availableDivisions.findIndex(
          (division) => division.id === this.selectedDivisionId
        )
        if (e.direction == 2) {
          if (currentIndex === this.availableDivisions.length - 1) {
            this.selectedDivisionId = this.availableDivisions[0].id
          } else {
            this.selectedDivisionId =
              this.availableDivisions[currentIndex + 1].id
          }
        } else if (e.direction == 4) {
          if (currentIndex === 0) {
            this.selectedDivisionId =
              this.availableDivisions[this.availableDivisions.length - 1].id
          } else {
            this.selectedDivisionId =
              this.availableDivisions[currentIndex - 1].id
          }
        }

        this.updateRoute()
      }
    },
    availableDivisionsInPool(poolId) {
      return this.eventDivisionPools.pools.filter(
        (division) => division.poolId == poolId
      )
    },
    updateRoute() {
      if (this.selectedPoolId === null) {
        this.$router.push({
          name: 'public-event-division-results',
          params: {
            eventId: this.$route.params.eventId,
            divisionId: this.selectedDivisionId,
          },
        })
      } else {
        const newDivisionIds = this.availableDivisionsInPool(
          this.selectedPoolId
        )
        const divisionExistInNewPool = newDivisionIds.find(
          (division) => division.divisionId == this.selectedDivisionId
        )

        if (!divisionExistInNewPool && newDivisionIds.length === 0) {
          this.$router.push({
            name: 'public-event-division-pool-results',
            params: {
              poolId: this.selectedPoolId,
            },
          })
        } else {
          this.$router.push({
            name: 'public-event-division-pool-results',
            params: {
              poolId: this.selectedPoolId,
              divisionId:
                this.$route.params.poolId == this.selectedPoolId
                  ? this.selectedDivisionId
                  : divisionExistInNewPool
                  ? divisionExistInNewPool.divisionId
                  : newDivisionIds[0]?.divisionId,
            },
          })
        }
      }
    },
    async loadData() {
      let poolData = await this.$axios({
        data: {
          query: `
          {
            pool(poolId:"${this.$router.currentRoute.params.poolId}"){
              id
              status
              round{
                pools{
                  id
                }
                event{
                  divisions{
                    id
                    name
                  }
                  divisionInPool {
                      poolId
                      divisionId
                  }
                  singles
                  scoringAs
                  rounds{
                    pools{
                      id
                      name
                    }
                  }
                }
              }
            }
          }
          `,
        },
      })

      this.poolData = poolData.data.data.pool
      this.selectedDivisionId = this.$route.params.divisionId ?? null
      this.selectedPoolId = this.$route.params.poolId

      const divisionsInPool = {
        divisions: poolData.data.data.pool.round.event.divisions,
        pools: poolData.data.data.pool.round.event.divisionInPool,
      }

      this.$store.commit('updateEventDivisionPools', divisionsInPool)
      this.loaded = true
    },
  },
  beforeMount() {
    this.selectedDivisionId = this.$route.params.divisionId
    this.selectedPoolId = this.$route.params.poolId

    this.loadData()
  },
  beforeCreate() {},
  destroyed() {},
}
</script>

<style lang="scss" scoped>
.desktop-nav {
  display: none;
}

.mobile-nav {
  margin: 0 12px 0 12px;
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  justify-content: stretch;

  .division-selector,
  .round-selector {
    margin: 0;
    height: 40px;
    background-position-x: calc(100% - 12px);
    border: 1px solid $midnight;
    border-radius: 6px;
    width: auto;
    padding-right: 40px;
    flex-grow: 1;
    flex-basis: 0;

    &:disabled {
      color: $strom;
      border: 1px solid $fog;
    }
  }

  .division-selector {
    flex-basis: 65%;
  }

  .round-selector {
    flex-shrink: 2;
    max-width: 100%;
  }
}

.placeholder-message {
  margin: 48px auto 0 auto;
  display: flex;
  justify-content: center;
}

@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 992px) {
  .placeholder-message {
    margin: 72px auto 0 auto;
  }
}

@media (min-width: 1200px) {
  .mobile-nav {
    display: none;
  }

  .desktop-nav {
    z-index: 0;
    display: flex;
    align-items: center;
    margin-bottom: 64px;

    .action-menu {
      z-index: 4;
    }

    .selector-wrapper {
      display: flex;
      align-items: center;
      border: 1px solid $midnight;
      padding: 15px 16px;
      border-radius: 6px;

      &.inactive {
        p {
          color: $strom;
        }
      }

      p {
        font-size: 16px;
        margin: 0;
        line-height: 1rem;
      }

      svg {
        margin-left: 12px;
        width: 16px;
        height: 12px;
      }
    }

    .round-select {
      margin-right: 16px;
    }
  }
}
</style>
